/**
 * DataBest — Logo SVG components.
 *
 * Three variants are exported so the client can pick their favorite in F0:
 *   1. <DataBestLogoShieldNetwork />  — blue shield + green network node (data + protection)
 *   2. <DataBestLogoShieldLeaf />     — blue outlined shield + green leaf (growth + care)
 *   3. <DataBestLogoShieldCheck />    — solid blue shield + green checkmark (compliance + task done)
 *
 * All accept props:
 *   - size:      pixel size (default 48)
 *   - withWordmark: boolean; if true, renders the "DataBest" wordmark next to the mark
 *   - variantId: optional string for the SVG <defs> id (avoids collisions if multiple render on one page)
 *   - className: optional wrapper class
 */

import React from "react";

const BLUE = "#365895";
const BLUE_DARK = "#26395E";
const GREEN = "#00B050";
const WHITE = "#FFFFFF";

const Wordmark = ({ dark = false }) => (
    <div className="flex flex-col leading-none">
        <span
            className={`font-heading text-2xl font-extrabold tracking-tight ${
                dark ? "text-white" : "text-brand-blue-900"
            }`}
        >
            DataBest
        </span>
        <span
            className={`mt-0 flex flex-col text-[0.48rem] uppercase tracking-[0.22em] font-semibold leading-tight ${
                dark ? "text-slate-300" : "text-brand-blue-600"
            }`}
        >
            <span>Individual</span>
            <span>Evaluation</span>
            <span>Progress</span>
        </span>
    </div>
);

/* ============================================================
   Variant 1 — Shield + Network node
   ============================================================ */
export const DataBestLogoShieldNetwork = ({
    size = 48,
    withWordmark = false,
    dark = false,
    className = "",
    variantId = "v1",
}) => {
    const gradId = `db-shield-net-${variantId}`;
    return (
        <div
            className={`inline-flex items-center gap-3 ${className}`}
            data-testid="databest-logo-network"
        >
            <svg
                width={size}
                height={size}
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="DataBest logo — Shield with Network"
                role="img"
            >
                <defs>
                    <linearGradient
                        id={gradId}
                        x1="0"
                        y1="0"
                        x2="64"
                        y2="64"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0%" stopColor={BLUE} />
                        <stop offset="100%" stopColor={BLUE_DARK} />
                    </linearGradient>
                </defs>
                {/* Shield */}
                <path
                    d="M32 3 L58 12 V32 C58 47 46.5 57 32 61 C17.5 57 6 47 6 32 V12 Z"
                    fill={`url(#${gradId})`}
                />
                {/* Network nodes */}
                <circle cx="22" cy="24" r="3.4" fill={WHITE} />
                <circle cx="42" cy="24" r="3.4" fill={WHITE} />
                <circle cx="32" cy="42" r="5" fill={GREEN} />
                {/* Connecting lines */}
                <line
                    x1="22"
                    y1="24"
                    x2="32"
                    y2="42"
                    stroke={WHITE}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    opacity="0.9"
                />
                <line
                    x1="42"
                    y1="24"
                    x2="32"
                    y2="42"
                    stroke={WHITE}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    opacity="0.9"
                />
                <line
                    x1="22"
                    y1="24"
                    x2="42"
                    y2="24"
                    stroke={WHITE}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    opacity="0.55"
                />
            </svg>
            {withWordmark && <Wordmark dark={dark} />}
        </div>
    );
};

/* ============================================================
   Variant 2 — Shield outline + Leaf
   ============================================================ */
export const DataBestLogoShieldLeaf = ({
    size = 48,
    withWordmark = false,
    dark = false,
    className = "",
    variantId = "v2",
}) => {
    return (
        <div
            className={`inline-flex items-center gap-3 ${className}`}
            data-testid="databest-logo-leaf"
        >
            <svg
                width={size}
                height={size}
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="DataBest logo — Shield with Leaf"
                role="img"
            >
                {/* Shield outline */}
                <path
                    d="M32 3 L58 12 V32 C58 47 46.5 57 32 61 C17.5 57 6 47 6 32 V12 Z"
                    fill={WHITE}
                    stroke={BLUE}
                    strokeWidth="3.5"
                    strokeLinejoin="round"
                />
                {/* Leaf: two curves creating an organic leaf shape */}
                <path
                    d="M22 40 C22 26 32 18 44 18 C44 32 36 42 22 40 Z"
                    fill={GREEN}
                />
                {/* Leaf central vein */}
                <path
                    d="M23.5 38.5 C30 30 37 24 43 20"
                    stroke={WHITE}
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    fill="none"
                />
            </svg>
            {withWordmark && <Wordmark dark={dark} />}
        </div>
    );
};

/* ============================================================
   Variant 3 — Solid blue shield + Green checkmark
   ============================================================ */
export const DataBestLogoShieldCheck = ({
    size = 48,
    withWordmark = false,
    dark = false,
    className = "",
    variantId = "v3",
}) => {
    const gradId = `db-shield-check-${variantId}`;
    return (
        <div
            className={`inline-flex items-center gap-3 ${className}`}
            data-testid="databest-logo-check"
        >
            <svg
                width={size}
                height={size}
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-label="DataBest logo — Shield with Checkmark"
                role="img"
            >
                <defs>
                    <linearGradient
                        id={gradId}
                        x1="0"
                        y1="0"
                        x2="64"
                        y2="64"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop offset="0%" stopColor={BLUE} />
                        <stop offset="100%" stopColor={BLUE_DARK} />
                    </linearGradient>
                </defs>
                {/* Shield */}
                <path
                    d="M32 3 L58 12 V32 C58 47 46.5 57 32 61 C17.5 57 6 47 6 32 V12 Z"
                    fill={`url(#${gradId})`}
                />
                {/* Bold checkmark in green with a white outer stroke for depth */}
                <path
                    d="M18 33 L28 43 L46 22"
                    stroke={WHITE}
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
                <path
                    d="M18 33 L28 43 L46 22"
                    stroke={GREEN}
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                />
            </svg>
            {withWordmark && <Wordmark dark={dark} />}
        </div>
    );
};

/**
 * <DataBestLogo> — Default export.
 * A single component that picks a variant by prop (defaults to the checkmark variant).
 */
const VARIANTS = {
    network: DataBestLogoShieldNetwork,
    leaf: DataBestLogoShieldLeaf,
    check: DataBestLogoShieldCheck,
};

const DataBestLogo = ({ variant = "check", ...rest }) => {
    const Component = VARIANTS[variant] || DataBestLogoShieldCheck;
    return <Component {...rest} />;
};

export default DataBestLogo;
